import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AlertService } from 'core/services/alert.service';
import { DialogService } from 'core/services/dialog.service';
import { StorageService } from 'core/services/storage.service';
import { TranslationService } from 'core/services/translation.service';
import { UtilsService } from 'core/services/utils.service';
import { ChangePlanConfirmation, ChangePlanConfirmationDialogData } from 'features/settings/components/change-plan-confirmation/change-plan-confirmation';
import { ChangeSubscriptionPlanRequestDTO } from 'features/settings/dtos/change-subscription-plan-request.dto';
import { SettingsService } from 'features/settings/settings.service';
import { PurchaseTrialedSubscriptionRequestDTO } from 'features/subscriptions/dtos/purchase-trialed-subscription-request.dto';
import { SubscriptionsService } from 'features/subscriptions/subscriptions.service';
import { SwitchField } from 'shared/components/particles/forms/switch-field/switch-field';
import { Icon } from 'shared/components/particles/icon/icon';
import { EXTERNAL_LINK_TARGET, SUBSCRIPTION_INTERVAL } from 'shared/enums/common.enum';
import { Subscription } from 'shared/models/subscriptions/subscription';
import { formatCurrency } from 'shared/utils/currency-format.util';

@Component({
  selector: 'app-change-plan',
  imports: [TranslateModule, CommonModule, Icon, SwitchField],
  providers: [
    SubscriptionsService,
    SettingsService
  ],
  templateUrl: './change-plan.html',
  styleUrl: './change-plan.scss',
})
export class ChangePlan implements OnInit {

  plans: Subscription[] = [];
  currentPlan: string = ''
  isAnnualBilling: boolean = false;

  constructor(
    private subscriptionsService: SubscriptionsService,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<ChangePlan>,
    private settingService: SettingsService,
    private alertService: AlertService,
    private translationService: TranslationService,
    private dialogService: DialogService,
    private storageService: StorageService,
    private utilService: UtilsService
  ) { }

  onClose(isSuccess: boolean = false) {
    this.dialogRef.close(isSuccess);
  }

  onSelectPlan(plan: Subscription) {
    this.currentPlan = plan.id;
  }

  toggleBillingCycle(checked: boolean) {
    this.isAnnualBilling = checked;
  }

  getDisplayPrice(plan: Subscription): string {
    const price = this.isAnnualBilling ? plan.yearlyPrice : plan.price;
    return formatCurrency(price);
  }

  getDisplayPeriod(): string {
    return this.isAnnualBilling ? '/year' : '/month';
  }

  getBillingNote(): string {
    return this.isAnnualBilling ? 'billedAnnually' : 'billedMonthly';
  }

  getMonthlyEquivalent(plan: Subscription): string {
    if (!this.isAnnualBilling || !plan.yearlyPrice) {
      return '';
    }

    return `${formatCurrency(plan.yearlyPrice / 12)}/mo`;
  }

  getAnnualSavingsAmount(plan: Subscription): number {
    if (!this.isAnnualBilling || !plan.price || !plan.yearlyPrice) {
      return 0;
    }

    return Math.max((plan.price * 12) - plan.yearlyPrice, 0);
  }

  getAnnualSavingsPercent(plan: Subscription): number {
    const yearlyFromMonthly = plan.price * 12;
    const savingsAmount = this.getAnnualSavingsAmount(plan);
    if (!yearlyFromMonthly || !savingsAmount) {
      return 0;
    }

    return Math.round((savingsAmount / yearlyFromMonthly) * 100);
  }

  getAnnualSavingsLabel(plan: Subscription): string {
    const savingsAmount = this.getAnnualSavingsAmount(plan);

    if (!savingsAmount) {
      return '';
    }

    return `Save ${formatCurrency(savingsAmount)}/yr if paid annually`;
  }

  getPlanLimitLabels(plan: Subscription): string[] {
    const labels: string[] = [];

    if (this.hasPositiveLimit(plan.maxUsers)) {
      labels.push(this.getUsersLabel(plan.maxUsers));
    }

    if (this.hasPositiveLimit(plan.maxWarehouses)) {
      labels.push(this.getCountLabel(plan.maxWarehouses, 'location', 'locations'));
    }

    if (this.hasPositiveLimit(plan.maxServiceTrucks)) {
      labels.push(this.getCountLabel(plan.maxServiceTrucks, 'service truck', 'service trucks'));
    }

    return labels;
  }

  getPlanAllowedLabels(plan: Subscription): string[] {
    return this.getPlanLimitLabels(plan).map(label => `${label} included`);
  }

  getPlanFeatureLabels(plan: Subscription): string[] {
    const features = (plan.features || []).filter(feature => !!feature?.trim());
    return [...this.getPlanAllowedLabels(plan), ...features];
  }

  getUsersLabel(maxUsers: number): string {
    return maxUsers >= 100 ? 'Unlimited users' : this.getCountLabel(maxUsers, 'user', 'users');
  }

  private getCountLabel(count: number, singular: string, plural: string): string {
    return `${count} ${count === 1 ? singular : plural}`;
  }

  private hasPositiveLimit(value: unknown): value is number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0;
  }

  getSubscriptions() {
    this.subscriptionsService.getSubscriptions().subscribe({
      next: (resp) => {
        const subscriptions = resp.data;
        this.plans = subscriptions.map((sub) => new Subscription(sub));
        this.cdr.markForCheck()
      }
    })
  }

  get currentSubscription() {
    const { subscription } = this.storageService.currentSubscription;
    return subscription.id;
  }

  get usePurchaseTrialedSubscription(): boolean {
    return (this.storageService.isTrial || !this.storageService.currentSubscription)
  }

  get dialogLabel(): string {
    return this.usePurchaseTrialedSubscription ? 'purchasePlan' : 'changePlan';
  }

  async handleChangeSubscription() {
    if (this.currentPlan) {
      if(this.usePurchaseTrialedSubscription) {
        this.purchaseTrialedSubscription();
        return;
      }
      const dialog = this.dialogService.openDialog(ChangePlanConfirmation, this.changePlanConfirmationData, true);
      dialog.afterClosed().subscribe({
        next: (result) => {
          if (result?.isConfirmed) {
            const body = new ChangeSubscriptionPlanRequestDTO({
              interval: this.isAnnualBilling ? SUBSCRIPTION_INTERVAL.YEAR : SUBSCRIPTION_INTERVAL.MONTH
            })
            this.settingService.changeSubscription(this.currentPlan, body).subscribe({
              next: (resp) => {
                const { isSuccess = false, message = '' } = resp;
                if (isSuccess) {
                  this.alertService.successToaster(message);
                  this.onClose(true);
                }
              }
            })
          }
        }
      })
    } else {
      const translation = this.translationService.getTranslation('plsSelectPlan');
      this.alertService.warningToaster(translation);
    }
  }

  purchaseTrialedSubscription() {
    const body = new PurchaseTrialedSubscriptionRequestDTO(
      { interval: this.isAnnualBilling ? SUBSCRIPTION_INTERVAL.YEAR : SUBSCRIPTION_INTERVAL.MONTH, subscriptionId: this.currentPlan }
    );
    this.subscriptionsService.purchaseTrialedSubscription(body).subscribe({
      next: (resp) => {
        const { isSuccess = false, data } = resp;
        if (isSuccess) {
          this.utilService.goToExternalLink(data.checkoutUrl, EXTERNAL_LINK_TARGET.SELF);
        }
      }
    })
  }

  get selectedPlan() {
    return this.plans.find(plan => plan.id === this.currentPlan);
  }

  get changePlanConfirmationData(): ChangePlanConfirmationDialogData | null {
    if (!this.selectedPlan) {
      return null;
    }

    return {
      title: this.selectedPlan.name,
      price: this.isAnnualBilling ? this.selectedPlan.yearlyPrice : this.selectedPlan.price,
      per: this.isAnnualBilling ? SUBSCRIPTION_INTERVAL.YEAR : SUBSCRIPTION_INTERVAL.MONTH,
      users: this.selectedPlan.maxUsers,
      location: this.selectedPlan.maxWarehouses,
      detail: this.getPlanFeatureLabels(this.selectedPlan)
    };
  }

  get confirmChangeBtnLabel(): string {
    if(this.usePurchaseTrialedSubscription) {
      return 'startSubscription';
    }else {
      return 'confirmChange'
    }
  }

  get dialogDescription(): string {
    if(this.storageService.isTrial) {
      return 'planPurchaseEndsTrialDesc'; 
    }else if(!this.storageService.currentSubscription) {
      return 'changePlanTrialEndedDesc'
    }else {
      return 'changePlanDesc'
    }
  }

  presetCurrentPlan() {
    if(this.usePurchaseTrialedSubscription) {
      this.currentPlan = this.storageService.lastSubscriptionId;
    }else {
      const subscriptionId = this.storageService.currentSubscription?.subscription?.id
      this.currentPlan = subscriptionId;
    }
  }

  ngOnInit(): void {
    this.getSubscriptions();
    // this.currentPlan = this.storageService.currentSubscription?.subscription?.id ?? '';
    this.presetCurrentPlan();
  }
}
